import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errText, downloadFile } from "../../lib/api";
import { DownloadSimple, Play } from "@phosphor-icons/react";
import { AdjustmentsPanel } from "../../components/admin/payroll/AdjustmentsPanel";
import { LoansPanel } from "../../components/admin/payroll/LoansPanel";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function Payroll() {
  const [tab, setTab] = useState("run");
  const [runs, setRuns] = useState([]);
  const [month, setMonth] = useState(thisMonth());
  const [slips, setSlips] = useState([]);
  const [viewMonth, setViewMonth] = useState("");
  const [busy, setBusy] = useState(false);

  const loadRuns = useCallback(() => api.get("/payroll/runs").then((r) => {
    setRuns(r.data);
    if (r.data.length) setViewMonth((v) => v || r.data[0].month);
  }), []);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  useEffect(() => {
    if (viewMonth) api.get("/payroll/payslips", { params: { month: viewMonth } }).then((r) => setSlips(r.data));
  }, [viewMonth]);

  const selectMonth = (m) => setViewMonth(m);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/payroll/run/${month}`);
      toast.success(`Payroll processed for ${month} — ${r.data.employee_count} employees`);
      loadRuns(); selectMonth(month);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const exp = (kind) => {
    const ext = kind === "ecr" ? "txt" : "csv";
    downloadFile(`/payroll/export/${viewMonth}/${kind}`, `${kind}_${viewMonth}.${ext}`).catch((e) => toast.error(errText(e)));
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight">Payroll</h1>

      <div className="flex gap-2 flex-wrap">
        {[["run", "Run & payslips"], ["adjustments", "Reimbursements & deductions"], ["loans", "Loans & advances"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`payroll-tab-${k}`}
            className={`px-5 py-2.5 rounded-full text-sm font-bold transition-colors duration-200 ${tab === k ? "bg-secondary text-white" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>{label}</button>
        ))}
      </div>

      {tab === "adjustments" && <AdjustmentsPanel />}
      {tab === "loans" && <LoansPanel />}

      {tab === "run" && (<>
      <div className="bg-white border border-stone-200 rounded-lg p-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-sm font-semibold text-stone-700">Payroll month</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="payroll-month-input"
            className="mt-1 block min-h-[44px] px-3 rounded-lg border border-stone-300 bg-white" />
        </div>
        <button onClick={run} disabled={busy} data-testid="run-payroll-button"
          className="tap-scale flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-bold hover:bg-[#A63F07] transition-colors duration-200 disabled:opacity-60">
          <Play size={18} weight="fill" /> {busy ? "Processing…" : "Run payroll"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-stone-200 rounded-lg divide-y divide-stone-100 self-start">
          <div className="p-4 font-heading font-bold">Processed months</div>
          {runs.length === 0 && <p className="p-6 text-sm text-stone-400">No payroll runs yet.</p>}
          {runs.map((r) => (
            <button key={r.id} onClick={() => selectMonth(r.month)} data-testid={`payroll-run-${r.month}`}
              className={`w-full text-left p-4 hover:bg-stone-50 transition-colors duration-150 ${viewMonth === r.month ? "bg-accent" : ""}`}>
              <div className="font-semibold text-sm tabular-nums">{r.month}</div>
              <div className="text-xs text-stone-500">{r.employee_count} employees · Net ₹{r.total_net?.toLocaleString("en-IN")}</div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 space-y-4">
          {viewMonth && (
            <div className="flex flex-wrap gap-2">
              {[["pf", "PF export"], ["ecr", "ECR (.txt)"], ["esic", "ESIC export"], ["tds", "TDS export"], ["register", "Payroll register"]].map(([k, label]) => (
                <button key={k} onClick={() => exp(k)} data-testid={`export-${k}-button`}
                  className="tap-scale flex items-center gap-2 px-4 py-2.5 rounded-full bg-secondary text-white text-sm font-bold hover:bg-[#1E2B22] transition-colors duration-200">
                  <DownloadSimple size={16} /> {label}
                </button>
              ))}
            </div>
          )}
          <div className="bg-white border border-stone-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-stone-500 border-b border-stone-200">
                  <th className="px-5 py-4">Employee</th>
                  <th className="px-5 py-4 text-right">Gross</th>
                  <th className="px-5 py-4 text-right hidden md:table-cell">LOP</th>
                  <th className="px-5 py-4 text-right hidden sm:table-cell">Deductions</th>
                  <th className="px-5 py-4 text-right">Net pay</th>
                  <th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {slips.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-stone-400">Select a processed month to view payslips.</td></tr>}
                {slips.map((s) => (
                  <tr key={s.id}>
                    <td className="px-5 py-3"><span className="font-semibold">{s.employee_name}</span><div className="text-xs text-stone-500">{s.employee_code}</div></td>
                    <td className="px-5 py-3 text-right tabular-nums">₹{s.gross.toLocaleString("en-IN")}</td>
                    <td className="px-5 py-3 text-right tabular-nums hidden md:table-cell">{s.lop_days > 0 ? <span className="text-red-700 font-semibold">{s.lop_days}d</span> : "—"}</td>
                    <td className="px-5 py-3 text-right tabular-nums hidden sm:table-cell">₹{s.total_deductions.toLocaleString("en-IN")}</td>
                    <td className="px-5 py-3 text-right tabular-nums font-bold">₹{s.net_pay.toLocaleString("en-IN")}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => downloadFile(`/payroll/payslips/${s.id}/pdf`, `payslip_${s.month}_${s.employee_code}.pdf`).catch((e) => toast.error(errText(e)))}
                        data-testid={`payslip-pdf-${s.id}`} className="text-primary font-bold text-xs hover:underline">PDF</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </>)}
    </div>
  );
}
